import { useCallback, useMemo, useRef, useState } from 'react';
import algoliasearch from 'algoliasearch/lite';
import {
  AlgoliaResponse,
  Resource,
  ResourceCategoryClass
} from '@setvi/shared/interfaces';
import { getResourceFromAlgoliaHit } from '@setvi/shared/utils';
import { RESOURCE_PAGINATION } from '@setvi/shared/enums';

export enum IndexType {
  QUICK = 'Quick',
  DEEP = 'Deep'
}

interface UseAlgoliaProps {
  sasToken: string;
  algoliaKey: string;
  hitsPerPage?: number;
  filters?: string;
}

// Algolia answers a query longer than this (a pasted block of text, say) with
// a 400, so the query is cut to the limit before it is sent.
const MAX_QUERY_BYTES = 512;

const clampQuery = (query: string) => {
  const bytes = new TextEncoder().encode(query);
  if (bytes.length <= MAX_QUERY_BYTES) return query;
  // A multi-byte character cut in half decodes to U+FFFD; drop it.
  return new TextDecoder()
    .decode(bytes.slice(0, MAX_QUERY_BYTES))
    .replace(/\uFFFD$/, '');
};

const breadcrumbFilter = 'breadcrumbs.resourceCategoryClass';
export const resourceFilter = `${breadcrumbFilter}=${ResourceCategoryClass.RESOURCES}`;
export const myResourceFilter = `${breadcrumbFilter}=${ResourceCategoryClass.MY_RESOURCES}`;

export const useAlgolia = ({
  sasToken,
  algoliaKey,
  hitsPerPage = RESOURCE_PAGINATION.LIMIT,
  filters
}: UseAlgoliaProps) => {
  const [resources, setResources] = useState<Resource[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isResultInsideDocuments, setIsResultInsideDocuments] =
    useState<boolean>(false);
  const lastQuery = useRef(null);

  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const client = useMemo(
    () => algoliasearch(process.env.ALGOLIA_APP_ID, algoliaKey),
    [algoliaKey]
  );
  const index = useMemo(
    () => client.initIndex(process.env.ALGOLIA_INDEX),
    [client]
  );
  const textIndex = useMemo(
    () => client.initIndex(process.env.ALGOLIA_TEXT_INDEX),
    [client]
  );

  const handleAlgoliaResponse = useCallback(
    (
      response: AlgoliaResponse,
      isInsideDocuments: boolean,
      goToNextPage = false
    ) => {
      // @ts-ignore for property mismatch on algolia
      const res: Partial<Resource[]> = response.hits.map(resource =>
        getResourceFromAlgoliaHit(resource, sasToken)
      );

      setResources(loadedResources => {
        const result = goToNextPage ? [...loadedResources, ...res] : res;
        return result;
      });
      setIsLoading(false);
      setIsResultInsideDocuments(isInsideDocuments);
      setPage(response.page);
      setTotalCount(response.nbHits);
      return res;
    },
    [sasToken]
  );

  const searchAlgolia = useCallback(
    (
      query: string,
      indexType: IndexType.QUICK | IndexType.DEEP,
      goToNextPage = false
    ): Promise<AlgoliaResponse> =>
      new Promise((resolve, reject) => {
        lastQuery.current = query;
        const idx = indexType === IndexType.QUICK ? index : textIndex;

        idx
          .search(clampQuery(query), {
            filters,
            hitsPerPage,
            distinct: 1,
            ...(goToNextPage ? { page: page + 1 } : {})
          })
          .then((response: AlgoliaResponse) => {
            // A response to a query the user has since typed over is dropped.
            if (lastQuery.current === query) resolve(response);
          })
          .catch(reject);
      }),
    [index, textIndex, hitsPerPage, filters, page]
  );

  const searchAlgoliaResources = useCallback(
    (
      query: string,
      autoStartDeepSearch = false,
      isDeepSearch = false,
      goToNextPage = false
    ) => {
      setIsLoading(true);
      const request = isDeepSearch
        ? searchAlgolia(query, IndexType.DEEP, goToNextPage).then(response =>
            handleAlgoliaResponse(response, true, goToNextPage)
          )
        : searchAlgolia(query, IndexType.QUICK, goToNextPage).then(response => {
            if (response.hits.length === 0 && autoStartDeepSearch)
              return searchAlgolia(query, IndexType.DEEP).then(deepResponse =>
                handleAlgoliaResponse(deepResponse, true, goToNextPage)
              );
            return handleAlgoliaResponse(response, false, goToNextPage);
          });

      // A failed search shows no results rather than a loader that never ends;
      // a failed next page keeps what is already on screen.
      request.catch(() => {
        if (!goToNextPage) setResources([]);
        setIsLoading(false);
      });
    },
    [searchAlgolia, handleAlgoliaResponse]
  );

  const loadNextAlgoliaResult = useCallback(
    (query: string) => {
      if (resources.length === totalCount) return;
      searchAlgoliaResources(query, false, false, true);
    },
    [searchAlgoliaResources, resources, totalCount]
  );

  return {
    searchAlgoliaResources,
    isResultInsideDocuments,
    resources,
    isLoading,
    loadNextAlgoliaResult,
    totalCount
  };
};
